import { useEffect, useMemo, useState } from 'react';

import { resolveDocumentViewerLayout } from '@/utils/documentViewerLayout/documentViewerLayout';
import { measureDocumentViewerSpace } from '@/utils/documentViewerLayout/measureSpace';
import type { TDocumentViewerLayout, TDocumentViewerSpace } from '@/utils/documentViewerLayout/types';

const HISTORY_SIDEBAR_SELECTOR = '.ApplicationLayoutSidebar:not(.right)';

/**
 * Keeps the viewer's mode and width limits in step with the window and the thread history.
 *
 * The history can be opened, closed or dragged while the Viewer is open, and none of that changes the
 * window width, which is why a `ResizeObserver` on it is needed next to the window's resize event, and
 * why rio's `switchModeBreakpoint` cannot make this decision.
 */
export const useDocumentViewerLayout = (): TDocumentViewerLayout => {
  const [space, setSpace] = useState<TDocumentViewerSpace>(measureDocumentViewerSpace);

  useEffect(() => {
    const update = () =>
      setSpace((current) => {
        const next = measureDocumentViewerSpace();
        const isUnchanged = next.windowWidth === current.windowWidth && next.historyWidth === current.historyWidth;
        return isUnchanged ? current : next;
      });

    // INFO: Measured once more on mount since the viewer can open before the history sidebar has settled at its
    // width, and the initial state was read during the first render.
    update();

    window.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    const historySidebar = document.querySelector(HISTORY_SIDEBAR_SELECTOR);
    if (historySidebar) observer.observe(historySidebar);

    return () => {
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, []);

  return useMemo(() => resolveDocumentViewerLayout(space), [space]);
};
